<template>
  <div id="settings-partner-admin" class="settings-container">
    <!-- Header com botão de voltar -->
    <div class="settings-header">
      <v-btn
        icon
        small
        @click="$router.back()"
        class="back-button"
      >
        <v-icon color="grey darken-3">mdi-arrow-left</v-icon>
      </v-btn>
    </div>

    <!-- Card centralizado -->
    <v-card
      id="settings-card"
      class="settings-card"
      elevation="0"
      outlined
    >
      <v-card-title class="justify-center pt-8">
        <div class="text-center">
          <v-avatar color="green lighten-4" size="56" class="mb-4">
            <v-icon color="green darken-2" size="32">mdi-account-tie</v-icon>
          </v-avatar>
          <div class="text-h5 font-weight-bold mt-4">Partner Admin</div>
          <div class="text-caption grey--text mt-1">Criar administrador para parceiros específicos</div>
        </div>
      </v-card-title>

      <v-card-text class="pa-8">
        <!-- Aviso de desenvolvimento -->
        <v-alert
          type="warning"
          text
          icon="mdi-alert"
          class="mb-6"
        >
          <strong>Em Desenvolvimento:</strong> Esta funcionalidade ainda não está conectada ao backend.
        </v-alert>

        <v-form
          ref="form"
          v-model="valid"
          lazy-validation
          class="form-container"
        >
          <div class="form-field">
            <v-text-field
              v-model="formData.name"
              label="Nome Completo *"
              :rules="[v => !!v || 'Nome é obrigatório']"
              outlined
              dense
              hint="Ex: Maria Santos"
            />
          </div>

          <div class="form-field">
            <v-text-field
              v-model="formData.email"
              label="Email *"
              :rules="[
                v => !!v || 'Email é obrigatório',
                v => /.+@.+\..+/.test(v) || 'Email deve ser válido',
              ]"
              outlined
              dense
              hint="Email do administrador parceiro"
            />
          </div>

          <div class="form-field">
            <v-text-field
              v-model="formData.firebaseUid"
              label="Firebase UID *"
              :rules="[v => !!v || 'Firebase UID é obrigatório']"
              outlined
              dense
              hint="UID do usuário no Firebase Authentication"
            />
          </div>

          <div class="form-field">
            <v-text-field
              v-model="formData.partnerId"
              label="Partner ID *"
              :rules="[v => !!v || 'Partner ID é obrigatório']"
              outlined
              dense
              hint="ID do parceiro (parâmetro de query)"
            />
          </div>

          <v-alert
            type="info"
            text
            dense
            class="mb-6"
          >
            ℹ️ O Partner Admin será criado com:
            <ul class="mt-2">
              <li><strong>Role:</strong> PARTNER_ADMIN (administrador de parceiro)</li>
              <li><strong>Partner ID:</strong> {{ formData.partnerId || '(não informado)' }}</li>
              <li><strong>Acesso:</strong> Gerenciar tenants do parceiro</li>
            </ul>
            <div class="mt-2">
              ⚠️ Certifique-se de que o <strong>Partner ID</strong> informado existe no sistema.
            </div>
          </v-alert>

          <!-- Botões de ação centralizado -->
          <div class="button-container">
            <v-btn
              color="green"
              rounded
              @click="createPartnerAdmin"
              :disabled="!valid"
              class="action-button"
            >
              <v-icon left>mdi-account-tie</v-icon>
              Criar Partner Admin
            </v-btn>
          </div>
        </v-form>

        <!-- Painel de informações do endpoint -->
        <v-expansion-panels class="mt-6">
          <v-expansion-panel>
            <v-expansion-panel-header>
              <div>
                <v-icon left color="green">mdi-information</v-icon>
                <strong>Informações do Endpoint Backend</strong>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-simple-table>
                <tbody>
                  <tr>
                    <td class="font-weight-bold">Método:</td>
                    <td><v-chip small color="success">POST</v-chip></td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold">URL:</td>
                    <td><code>/api/admin/bootstrap/partner-admin?partnerId={{ formData.partnerId || '{id}' }}</code></td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold">Query Param:</td>
                    <td><code>partnerId</code> (obrigatório)</td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold">Descrição:</td>
                    <td>Cria um administrador para um parceiro específico. Automaticamente recebe role PARTNER_ADMIN.</td>
                  </tr>
                </tbody>
              </v-simple-table>

              <v-divider class="my-4"></v-divider>

              <div class="font-weight-bold mb-2">Payload de exemplo:</div>
              <pre class="green lighten-5 pa-3" style="border-radius: 4px; overflow-x: auto;">{{ JSON.stringify(payloadData, null, 2) }}</pre>

              <div class="font-weight-bold mb-2 mt-4">URL completa:</div>
              <pre class="green lighten-5 pa-3" style="border-radius: 4px; overflow-x: auto;">POST /api/admin/bootstrap/partner-admin?partnerId={{ formData.partnerId || '{id}' }}</pre>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>
    </v-card>

    <!-- Snackbar de feedback -->
    <v-snackbar
      v-model="snackbar"
      :color="snackbarColor"
      :timeout="3000"
      top
    >
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="snackbar = false"
        >
          Fechar
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
export default {
  name: 'SettingsPartnerAdmin',

  data: () => ({
    valid: false,
    formData: {
      name: '',
      email: '',
      firebaseUid: '',
      partnerId: '',
    },
    snackbar: false,
    snackbarText: '',
    snackbarColor: 'success',
  }),

  computed: {
    payloadData () {
      // Retorna apenas os dados do body (sem partnerId)
      return {
        name: this.formData.name,
        email: this.formData.email,
        firebaseUid: this.formData.firebaseUid,
      }
    },
  },

  methods: {
    async createPartnerAdmin () {
      if (!this.$refs.form.validate()) {
        return
      }

      console.log('📤 Criar Partner Admin:')
      console.log('  Query Param partnerId:', this.formData.partnerId)
      console.log('  Body:', this.payloadData)

      // Simulação de chamada ao backend (mock)
      await new Promise(resolve => setTimeout(resolve, 1000))

      this.snackbarText = '✅ Partner Admin criado com sucesso! (mock)'
      this.snackbarColor = 'success'
      this.snackbar = true

      // Redirecionar após sucesso
      setTimeout(() => {
        this.$router.push('/settings')
      }, 2000)
    },
  },
}
</script>

<style scoped>
/* Container principal - estilo macOS */
#settings-partner-admin {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  background: linear-gradient(135deg, #F5F5F7 0%, #f0f2f5 100%);
}

.theme--dark #settings-partner-admin {
  background: linear-gradient(135deg, #121212 0%, #1C1C1E 100%);
}

/* Header com botão de voltar */
.settings-header {
  position: fixed;
  top: 1rem;
  left: 1rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  z-index: 100;
}

.back-button {
  transition: all 0.2s ease;
}

.back-button:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.theme--dark .back-button:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

/* Card centralizado - estilo macOS */
.settings-card {
  width: 100%;
  max-width: 500px;
  border-radius: 20px !important;
  background: white !important;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.08) !important;
  border: 1px solid rgba(0, 0, 0, 0.06) !important;
}

.theme--dark .settings-card {
  background: #1C1C1E !important;
  border-color: rgba(255, 255, 255, 0.1) !important;
}

/* Form container */
.form-container {
  width: 100%;
}

.form-field {
  margin-bottom: 1.5rem;
}

.form-field:last-of-type {
  margin-bottom: 0;
}

/* Button container centralizado */
.button-container {
  display: flex;
  justify-content: center;
  margin-top: 2rem;
}

.action-button {
  min-width: 200px;
}

/* Code styling */
code {
  background-color: #f5f5f5;
  padding: 2px 6px;
  border-radius: 3px;
  font-family: 'Courier New', monospace;
  font-size: 0.85em;
}

.theme--dark code {
  background-color: rgba(255, 255, 255, 0.1);
  color: #e8e8e8;
}

pre {
  font-family: 'Courier New', monospace;
  font-size: 13px;
  border-radius: 8px;
  overflow-x: auto;
}

/* Responsive */
@media (max-width: 600px) {
  #settings-partner-admin {
    padding: 0.5rem;
  }

  .settings-card {
    max-width: 100%;
    border-radius: 16px !important;
  }

  .settings-header {
    position: static;
    margin-bottom: 2rem;
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
